import { globalEventBus } from '@fiverr-private/futile';
import { LOGGER } from '../../helpers/eventName';
import { ERROR, INFO, severity } from '../../helpers/logLevels';
import { Interceptors } from '../../helpers/interceptors';
import isSufficientLevel from '../../helpers/isSufficientLevel';
import serialise from '../../helpers/serialise';

/**
 * "private" property
 * @type {Symbol|String}
 */
const minimal = typeof Symbol === 'function'
    ? Symbol()
    : '__minimal__'
;

/**
 * Listen to global event bus on LOGGER events
 * @property {Function} action
 * @property {String}   [logLevel='error']
 */
class Listener {
    constructor(action, { logLevel } = {}) {
        Object.defineProperty(
            this,
            'action',
            { value: action }
        );

        this.setLevel(logLevel);
        this.interceptors = new Interceptors();
        this.setEnricher = action.setEnricher;
    }

    /**
     * Minimal log level to output
     * @param  {String} logLevel
     * @return {Boolen} Whether the log level has changed
     */
    setLevel(logLevel = ERROR) {
        if (logLevel === this[minimal]) {
            return false;
        }

        this[minimal] = logLevel;

        return true;
    }

    /**
     * Add an interceptor.
     * @param {Function} interceptor
     */
    intercept(interceptor) {
        this.interceptors.add(interceptor);
    }

    /**
     * Send log if log level is enough
     * @param  {Object|String|Error} [options.data={}]        Any data
     * @param  {Object}              [options.enrichments={}] More fields of information
     * @param  {String}              [options.level='info']   This event's log level
     * @return {undefined}
     */
    log({ entity, enrichments = {}, level = INFO, force = false } = {}) {
        const interceptor = this.interceptors.run({
            entity,
            enrichments,
            minimalLevel: this[minimal],
            level,
            force
        });

        if (interceptor.blocked) {
            return;
        }

        const isSufficient = interceptor.force || isSufficientLevel(level, this[minimal]);

        if (!isSufficient) {
            return;
        }

        this.action(
            serialise(
                interceptor.entity,
                {
                    ...interceptor.enrichments,
                    level: interceptor.level,
                    time: Date.now(),
                    severity: severity(interceptor.level)
                }
            )
        );
    }
}

/**
 * Listen to global event bus on LOGGER events
 * @param {Function} options.action
 * @param {String}   [options.logLevel]
 * @return {Listener}
 */
export default function indifferentLoggerListener({ action, logLevel }) {
    if (globalEventBus.has(LOGGER)) {
        return;
    }

    const listener = new Listener(action, { logLevel });
    globalEventBus.on(LOGGER, listener.log.bind(listener));

    return listener;
}
